import { useEffect, useState } from 'react';
import { Search, Plus, X, Loader2 } from 'lucide-react';
import { adminLookupAPI } from '../../services/api';

/**
 * Searchable contact picker scoped to a company. Falls back to an inline
 * create-new-contact form when the caller isn't found in CW.
 *
 * Props:
 *   companyId — required. The CW company to search within.
 *   value — { id, name } | null. Selected contact.
 *   onChange — fn({ id, name }) | fn({ newContact: { firstName, lastName, email, phone, title } })
 */
export default function ContactPicker({ companyId, value, onChange, disabled }) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showResults, setShowResults] = useState(false);
  const [creating, setCreating] = useState(false);
  const [newContact, setNewContact] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    title: '',
  });

  useEffect(() => {
    if (!companyId || creating) return;
    if (query.trim().length < 2 && query.trim().length !== 0) return;

    const timer = setTimeout(async () => {
      setLoading(true);
      try {
        const resp = await adminLookupAPI.contactSearch(companyId, query.trim());
        setResults(resp.data?.items || []);
      } catch (err) {
        setResults([]);
      } finally {
        setLoading(false);
      }
    }, 250);
    return () => clearTimeout(timer);
  }, [companyId, query, creating]);

  if (value && !creating) {
    return (
      <div className="flex items-center justify-between gap-2 p-2 border border-neutral-300 dark:border-neutral-600 rounded-lg bg-neutral-50 dark:bg-neutral-700/50">
        <div>
          <div className="font-medium text-neutral-900 dark:text-white">
            {value.name || `${value.firstName || ''} ${value.lastName || ''}`.trim()}
          </div>
          {value.email && (
            <div className="text-xs text-neutral-500 dark:text-neutral-400">{value.email}</div>
          )}
          {value.isNew && (
            <div className="text-xs text-blue-500 dark:text-blue-400">
              Will be created in ConnectWise on submit
            </div>
          )}
        </div>
        <button
          type="button"
          onClick={() => onChange(null)}
          disabled={disabled}
          className="p-1 text-neutral-400 hover:text-red-500"
          title="Clear"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
    );
  }

  if (creating) {
    return (
      <div className="p-3 border border-neutral-300 dark:border-neutral-600 rounded-lg bg-neutral-50 dark:bg-neutral-700/50 space-y-2">
        <div className="flex items-center justify-between">
          <div className="text-sm font-medium text-neutral-700 dark:text-neutral-200">
            New contact
          </div>
          <button
            type="button"
            onClick={() => setCreating(false)}
            className="text-xs text-neutral-500 hover:text-neutral-700"
          >
            Cancel
          </button>
        </div>
        <div className="grid grid-cols-2 gap-2">
          <input
            type="text"
            placeholder="First name *"
            value={newContact.firstName}
            onChange={(e) => setNewContact({ ...newContact, firstName: e.target.value })}
            className="px-2 py-1 border border-neutral-300 dark:border-neutral-600 rounded bg-white dark:bg-neutral-800 text-sm"
          />
          <input
            type="text"
            placeholder="Last name *"
            value={newContact.lastName}
            onChange={(e) => setNewContact({ ...newContact, lastName: e.target.value })}
            className="px-2 py-1 border border-neutral-300 dark:border-neutral-600 rounded bg-white dark:bg-neutral-800 text-sm"
          />
          <input
            type="email"
            placeholder="Email"
            value={newContact.email}
            onChange={(e) => setNewContact({ ...newContact, email: e.target.value })}
            className="px-2 py-1 border border-neutral-300 dark:border-neutral-600 rounded bg-white dark:bg-neutral-800 text-sm col-span-2"
          />
          <input
            type="tel"
            placeholder="Phone"
            value={newContact.phone}
            onChange={(e) => setNewContact({ ...newContact, phone: e.target.value })}
            className="px-2 py-1 border border-neutral-300 dark:border-neutral-600 rounded bg-white dark:bg-neutral-800 text-sm"
          />
          <input
            type="text"
            placeholder="Title"
            value={newContact.title}
            onChange={(e) => setNewContact({ ...newContact, title: e.target.value })}
            className="px-2 py-1 border border-neutral-300 dark:border-neutral-600 rounded bg-white dark:bg-neutral-800 text-sm"
          />
        </div>
        <div className="flex justify-end">
          <button
            type="button"
            disabled={!newContact.firstName || !newContact.lastName}
            onClick={() => {
              onChange({
                newContact,
                name: `${newContact.firstName} ${newContact.lastName}`.trim(),
                email: newContact.email || undefined,
                isNew: true,
              });
              setCreating(false);
            }}
            className="px-3 py-1 text-sm bg-primary-500 text-white rounded hover:bg-primary-600 disabled:opacity-50"
          >
            Use this contact
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="relative">
      <div className="flex items-center gap-2">
        <div className="relative flex-1">
          <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-400" />
          <input
            type="text"
            value={query}
            onChange={(e) => {
              setQuery(e.target.value);
              setShowResults(true);
            }}
            onFocus={() => setShowResults(true)}
            placeholder={companyId ? 'Search contacts at this company…' : 'Pick a company first'}
            disabled={disabled || !companyId}
            className="w-full pl-8 pr-2 py-2 border border-neutral-300 dark:border-neutral-600 rounded-lg bg-white dark:bg-neutral-800 text-sm disabled:opacity-50"
          />
          {loading && (
            <Loader2 className="absolute right-2 top-1/2 -translate-y-1/2 w-4 h-4 animate-spin text-neutral-400" />
          )}
        </div>
        <button
          type="button"
          onClick={() => setCreating(true)}
          disabled={disabled || !companyId}
          className="flex items-center gap-1 px-3 py-2 text-sm border border-neutral-300 dark:border-neutral-600 rounded-lg hover:bg-neutral-100 dark:hover:bg-neutral-700 disabled:opacity-50"
        >
          <Plus className="w-4 h-4" />
          New
        </button>
      </div>
      {showResults && companyId && (
        <div className="absolute z-10 mt-1 w-full max-h-60 overflow-auto bg-white dark:bg-neutral-800 border border-neutral-200 dark:border-neutral-700 rounded-lg shadow-lg">
          {results.length === 0 && !loading ? (
            <div className="px-3 py-2 text-sm text-neutral-500 dark:text-neutral-400">
              {query.trim().length < 2
                ? 'Type to search contacts'
                : 'No contacts found — use "New" to add one'}
            </div>
          ) : (
            results.map((c) => {
              const name = `${c.firstName || ''} ${c.lastName || ''}`.trim() || c.identifier;
              const email = (c.communicationItems || []).find(
                (ci) => ci.communicationType === 'Email'
              )?.value;
              return (
                <button
                  type="button"
                  key={c.id}
                  onClick={() => {
                    onChange({ id: c.id, name, email });
                    setShowResults(false);
                    setQuery('');
                  }}
                  className="w-full text-left px-3 py-2 hover:bg-neutral-100 dark:hover:bg-neutral-700 text-sm"
                >
                  <div className="font-medium text-neutral-900 dark:text-white">{name}</div>
                  {email && (
                    <div className="text-xs text-neutral-500 dark:text-neutral-400">{email}</div>
                  )}
                </button>
              );
            })
          )}
        </div>
      )}
    </div>
  );
}
